// Firestore-lekérdezések.
//
// Pontosan ugyanazt a szerződést követi, mint a Flutter tasks_repo.getTasks():
//   1. projects/{companyId}/projects  -> a projektek (nem adminnál userIds szűréssel)
//   2. collectionGroup('taskChanges') -> az AKTUÁLIS változat projektenként
//      (companyId == cég, isCurrent == true)
//
// A "projects" név két különböző dolgot jelöl a Firestore-ban: a gyökér
// kollekció a CÉGET, az alkollekció a tényleges projektet. A kódban ezért
// mindenhol companyId / projectId szerepel, nem "projectId" mindkettőre.

import {
  collection,
  doc,
  getDoc,
  limit,
  orderBy,
  collectionGroup,
  getDocs,
  query,
  where,
  type DocumentData,
} from 'firebase/firestore';

import { t } from './i18n';
import { db } from './firebase';
import { describeRRule } from './rrule';
import {
  dateFrom,
  projectTasks,
  taskStatuses,
  type TaskStatus,
  type TaskView,
} from './model';

export type PersonView = {
  id: string;
  fullName: string;
  jobTitle?: string;
  photoUrl?: string;
  /// A bejelentkezéshez tartozó cím. A W24 jelszó-visszaállítója ezt küldi;
  /// a dokumentumon eddig is ott volt, csak a nézet nem vette át.
  email?: string;
  /// A kiosztott hely szintje. Az AI-s hely nyitja meg az „AI felvétel"
  /// menüpontot; a kiosztás a cégadmin dolga, a darabszám a Stripe-ból jön.
  seatTier?: 'basic' | 'ai';
};

/// A cég felhasználói.
///
/// A `users` gyökérkollekcióból olvasunk, és cégtagság szerint szűrünk — ezt
/// teszi a Flutter oldal is, mert a tagság a dokumentumon belüli tömbben él.
export async function loadPeople(companyId: string): Promise<PersonView[]> {
  const snapshot = await getDocs(collection(db, 'users'));
  const people: PersonView[] = [];

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data() as DocumentData;
    const memberships = Array.isArray(data['projects']) ? data['projects'] : [];
    const isMember = memberships.some(
      (m: unknown) =>
        typeof m === 'object' && m !== null && (m as { id?: unknown }).id === companyId,
    );
    if (!isMember) continue;

    const fullName = [data['lastName'], data['firstName']]
      .filter((p) => typeof p === 'string' && p.length > 0)
      .join(' ');

    people.push({
      id: docSnap.id,
      fullName: fullName.length > 0 ? fullName : String(data['email'] ?? docSnap.id),
      jobTitle: typeof data['jobTitle'] === 'string' ? data['jobTitle'] : undefined,
      photoUrl: typeof data['photoUrl'] === 'string' ? data['photoUrl'] : undefined,
      email: typeof data['email'] === 'string' ? data['email'] : undefined,
      // Ismeretlen erteket NEM ertelmezunk AI-kent: egy elgepelt vagy regi
      // mezoertek nem nyithat meg fizetos funkciot.
      seatTier: data['seatTier'] === 'ai' ? 'ai' : data['seatTier'] === 'basic' ? 'basic' : undefined,
    });
  }

  return people.sort((a, b) => a.fullName.localeCompare(b.fullName, 'hu'));
}

export type ProjectView = {
  id: string;
  /// Az AKTUALIS taskChange dokumentum azonositoja — az irasokhoz kell.
  currentChangeId?: string;
  title: string;
  description?: string;
  userIds: string[];
  tasks: TaskView[];
  isPeriodic: boolean;
  /// Az ismétlődés emberi leírása a tárolt RRULE-ból („Minden hónapban, a
  /// hónap 1. napján."). A terv (W18) a kártyán mutatja; eddig csak az
  /// „Ismétlődő" jelvény volt meg, az ütem sehol.
  recurrenceText?: string;
};

/// A cég projektjei a hozzájuk tartozó aktuális feladatokkal.
///
/// Nem adminnál csak azok a projektek jönnek, amelyekben a felhasználó benne
/// van (`userIds` tömb) — a jogosultságot a Firestore-szabályok is érvényesítik,
/// ez a szűrés csak a felesleges olvasást spórolja meg.
export async function loadProjects(options: {
  companyId: string;
  userId: string;
  isAdmin: boolean;
}): Promise<ProjectView[]> {
  const { companyId, userId, isAdmin } = options;

  const projectsRef = collection(db, 'projects', companyId, 'projects');
  const projectsQuery = isAdmin
    ? query(projectsRef)
    : query(projectsRef, where('userIds', 'array-contains', userId));

  const [projectSnap, changesSnap] = await Promise.all([
    getDocs(projectsQuery),
    getDocs(
      query(
        collectionGroup(db, 'taskChanges'),
        where('companyId', '==', companyId),
        where('isCurrent', '==', true),
      ),
    ),
  ]);

  // projectId -> az aktuális taskChange dokumentum (adat + azonosito)
  const currentByProject = new Map<string, { id: string; data: DocumentData }>();
  for (const docSnap of changesSnap.docs) {
    const data = docSnap.data();
    const projectId = data['projectId'];
    if (typeof projectId === 'string') {
      currentByProject.set(projectId, { id: docSnap.id, data });
    }
  }

  const now = new Date();

  return projectSnap.docs.map((docSnap) =>
    projectViewFrom(docSnap.id, docSnap.data(), currentByProject.get(docSnap.id), now),
  );
}

/// Egy projekt nézetté alakítása.
///
/// KÖZÖS függvény, mert két út vezet ide: a teljes betöltés (`loadProjects`)
/// és az egyetlen projekt újraolvasása (`reloadProject`). Ha a leképezés két
/// helyen élne, a mentés utáni frissítés némán MÁST mutatna, mint a lista —
/// és az ilyen eltérés csak akkor derülne ki, amikor már zavar.
function projectViewFrom(
  id: string,
  data: DocumentData,
  entry: { id: string; data: DocumentData } | undefined,
  now: Date,
): ProjectView {
  const current = entry?.data;
  return {
    id,
    currentChangeId: entry?.id,
    // A cím a taskChange dokumentumon él (az szerkeszthető), a projekt
    // dokumentumon csak tartalékként keressük.
    title: String(current?.['title'] ?? data['title'] ?? t('general.unnamedProject')),
    description:
      typeof current?.['description'] === 'string' ? current['description'] : undefined,
    userIds: Array.isArray(data['userIds']) ? (data['userIds'] as string[]) : [],
    tasks: current ? projectTasks(current, now) : [],
    isPeriodic: typeof current?.['periodicId'] === 'string',
    recurrenceText: describeRRule(
      typeof current?.['rrule'] === 'string' ? current['rrule'] : undefined,
    ),
  };
}

/// EGYETLEN projekt újraolvasása mentés után.
///
/// Miért kell: eddig minden írás után a teljes cég adata újratöltődött —
/// a projektek collection group lekérdezése, a felhasználók, öt törzsadat-
/// lekérdezés, a cég, a kiosztott helyek (ami MÉGEGYSZER végigolvassa a
/// felhasználókat) és az értesítések. Tíz körbefordulás egyetlen
/// státuszváltásért, amitől a mentés másodpercekig „gondolkodott".
///
/// Egy feladatírás viszont EGYETLEN projekt aktuális verzióját érinti. A
/// törzsadat (csoportok, munkakörök, mezők, sablonok, szerepkörök) tőle nem
/// változik, ezért nem is olvassuk újra.
///
/// Két dokumentumot kérünk le: a projektet és az általa mutatott aktuális
/// változatot. A `currentId`-t használjuk, mert az ismétlődés beállítása ÚJ
/// verziót hoz létre és átállítja a mutatót — a régi verzió azonosítójához
/// ragaszkodva a képernyőn a felülírt állapot maradna.
export async function reloadProject(
  companyId: string,
  projectId: string,
): Promise<ProjectView | undefined> {
  const projectRef = doc(db, 'projects', companyId, 'projects', projectId);
  const projectSnap = await getDoc(projectRef);
  if (!projectSnap.exists()) return undefined;

  const data = projectSnap.data();
  let entry: { id: string; data: DocumentData } | undefined;

  const currentId = data['currentId'];
  if (typeof currentId === 'string' && currentId.length > 0) {
    const changeSnap = await getDoc(doc(projectRef, 'taskChanges', currentId));
    if (changeSnap.exists()) entry = { id: changeSnap.id, data: changeSnap.data() };
  }

  // Tartalék: régebbi projekteken hiányozhat a `currentId` mutató.
  if (!entry) {
    const snapshot = await getDocs(
      query(collection(projectRef, 'taskChanges'), where('isCurrent', '==', true), limit(1)),
    );
    const first = snapshot.docs[0];
    if (first) entry = { id: first.id, data: first.data() };
  }

  return projectViewFrom(projectSnap.id, data, entry, new Date());
}

/// A tervek feladat-fülei (W05).
export type TaskTab = 'open' | 'mine' | 'previous' | 'all';

/// Szűrés a fül szerint.
///
/// A jelentések szándékosan az appéval egyeznek:
///   open     - ami rám vár és még nincs lezárva
///   mine     - amit én osztottam ki
///   previous - a saját, már lezárt feladataim
///   all      - minden, amit látok
export function filterTasks(
  projects: ProjectView[],
  tab: TaskTab,
  userId: string,
): TaskView[] {
  const closed: ReadonlySet<string> = new Set(['completed', 'failed', 'unaccepted']);
  const all = projects.flatMap((p) => p.tasks);

  switch (tab) {
    case 'open':
      return all.filter((t) => t.assigneeIds.includes(userId) && !closed.has(t.status));
    case 'mine':
      return all.filter((t) => t.submittedUserId === userId);
    case 'previous':
      return all.filter((t) => t.assigneeIds.includes(userId) && closed.has(t.status));
    case 'all':
      return all;
  }
}

// ---------------------------------------------------------------------------
// Cegszintu torzsadatok
// ---------------------------------------------------------------------------

/// Felhasznaloi csoportok: projects/{companyId}/groups
export async function loadGroups(
  companyId: string,
): Promise<{ id: string; name: string; userIds: string[] }[]> {
  const snapshot = await getDocs(collection(db, 'projects', companyId, 'groups'));
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: String(data['name'] ?? t('general.unnamedGroup')),
      userIds: Array.isArray(data['userIds']) ? (data['userIds'] as string[]) : [],
    };
  });
}

/// Munkakorok: projects/{companyId}/jobTitle
///
/// Az azonosito is visszajon, nem csak a nev: torles es atnevezes nelkule
/// lehetetlen lenne (nev szerint keresni torlendo dokumentumot torekeny —
/// ket azonos nevu munkakornel rossz sort talalna el).
export async function loadJobTitles(
  companyId: string,
): Promise<{ id: string; name: string }[]> {
  const snapshot = await getDocs(collection(db, 'projects', companyId, 'jobTitle'));
  return snapshot.docs
    .map((docSnap) => ({
      id: docSnap.id,
      name: String(docSnap.data()['name'] ?? ''),
    }))
    .filter((item) => item.name.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name, 'hu'));
}

/// Dinamikus mezodefiniciok: projects/{companyId}/fields
export async function loadFieldDefinitions(companyId: string): Promise<
  {
    id: string;
    name: string;
    type: 'text' | 'number' | 'date';
    showOnList: boolean;
    requiredInStatus?: TaskStatus;
  }[]
> {
  const snapshot = await getDocs(collection(db, 'projects', companyId, 'fields'));
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    const rawType = String(data['type'] ?? 'text');
    // A `status` mezo azt mondja meg, MELYIK statuszba lepeshez kotelezo a
    // kitoltese (lasd a Flutter `FieldModel.status`-t). Eddig kimaradt a
    // beolvasasbol, ezert a mezoszerkeszto ide vonatkozo valasztoja MINDIG
    // uresen nyilt — akkor is, ha az ertek el volt mentve.
    const rawStatus = data['status'];
    return {
      id: docSnap.id,
      name: String(data['name'] ?? ''),
      // A Flutter enum neve kerul a dokumentumba; ismeretlen ertekre szoveg.
      type: (['text', 'number', 'date'].includes(rawType) ? rawType : 'text') as
        | 'text'
        | 'number'
        | 'date',
      showOnList: data['isOnMainScreen'] === true,
      requiredInStatus: taskStatuses.includes(rawStatus as TaskStatus)
        ? (rawStatus as TaskStatus)
        : undefined,
    };
  });
}

/// A ceg felhasznaloinak szerepkore: users/{id}.projects[] -> role
export async function loadRoles(companyId: string): Promise<Record<string, string>> {
  const snapshot = await getDocs(collection(db, 'users'));
  const roles: Record<string, string> = {};
  for (const docSnap of snapshot.docs) {
    const memberships = Array.isArray(docSnap.data()['projects'])
      ? (docSnap.data()['projects'] as { id?: unknown; role?: unknown }[])
      : [];
    const membership = memberships.find((m) => m.id === companyId);
    if (membership && typeof membership.role === 'string') {
      roles[docSnap.id] = membership.role;
    }
  }
  return roles;
}

/// Projektsablonok: projects/{companyId}/taskTypes
///
/// A Flutter oldal (`TaskTypeBodyModel`) EGYETLEN tömbben tárolja a sablont:
/// az `order: 0` elem a sablon FEJE — az ő `task` mezője a sablon neve —, a
/// többi elem a feladatok, sorrendben. Ez a szerkezet, nem a miénk; ugyanezt
/// kell írni is, különben a mobil nem ismerné fel a sablont.
export async function loadProjectTemplates(companyId: string): Promise<
  { id: string; name: string; description?: string; tasks: { id?: string; title: string; description?: string }[] }[]
> {
  const snapshot = await getDocs(collection(db, 'projects', companyId, 'taskTypes'));

  return snapshot.docs
    .map((docSnap) => {
      const data = docSnap.data();
      const items = Array.isArray(data['taskTypes'])
        ? (data['taskTypes'] as Record<string, unknown>[])
        : [];

      const sorted = [...items].sort(
        (a, b) => Number(a['order'] ?? 0) - Number(b['order'] ?? 0),
      );
      const head = sorted.find((item) => Number(item['order'] ?? 0) === 0) ?? sorted[0];

      return {
        id: docSnap.id,
        name: String(head?.['task'] ?? t('general.unnamedTemplate')),
        description:
          typeof head?.['desc'] === 'string' && head['desc'].length > 0
            ? (head['desc'] as string)
            : undefined,
        tasks: sorted
          .filter((item) => item !== head)
          .map((item) => ({
            id: typeof item['id'] === 'string' ? item['id'] : undefined,
            title: String(item['task'] ?? ''),
            description:
              typeof item['desc'] === 'string' && item['desc'].length > 0
                ? (item['desc'] as string)
                : undefined,
          })),
      };
    })
    .filter((template) => template.name.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name, 'hu'));
}

/// Értesítések: users/{userId}/notifications
///
/// A Flutter oldal (`NotiRepo`) ugyanide ír és ugyaninnen olvas, `sentAt`
/// szerint csökkenő sorrendben. A `dateOfSeen` jelöli az olvasottságot —
/// külön `seen` logikai mező nincs.
export async function loadNotifications(userId: string): Promise<
  {
    id: string;
    title: string;
    body: string;
    sentAt: Date;
    seen: boolean;
    taskId?: string;
    commentId?: string;
  }[]
> {
  const snapshot = await getDocs(
    query(collection(db, 'users', userId, 'notifications'), orderBy('sentAt', 'desc')),
  );

  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      title: String(data['title'] ?? ''),
      body: String(data['body'] ?? ''),
      sentAt: dateFrom(data['sentAt']) ?? new Date(0),
      seen: typeof data['dateOfSeen'] === 'string' && data['dateOfSeen'].length > 0,
      taskId: typeof data['taskId'] === 'string' ? data['taskId'] : undefined,
      commentId: typeof data['commentId'] === 'string' ? data['commentId'] : undefined,
    };
  });
}
